import localizedLandingSlugs from "../i18n/target-landing-slugs.json" with { type: "json" };
import legacyUrlSlugs from "../i18n/legacy-url-slugs.json" with { type: "json" };
import deCourseSlugs from "../i18n/locales/de/app/course-slugs.json" with { type: "json" };
import enCourseSlugs from "../i18n/locales/en/app/course-slugs.json" with { type: "json" };
import arCourseSlugs from "../i18n/locales/ar/app/course-slugs.json" with { type: "json" };
import trCourseSlugs from "../i18n/locales/tr/app/course-slugs.json" with { type: "json" };
import ukCourseSlugs from "../i18n/locales/uk/app/course-slugs.json" with { type: "json" };
import ruCourseSlugs from "../i18n/locales/ru/app/course-slugs.json" with { type: "json" };
import esCourseSlugs from "../i18n/locales/es/app/course-slugs.json" with { type: "json" };
import viCourseSlugs from "../i18n/locales/vi/app/course-slugs.json" with { type: "json" };
import plCourseSlugs from "../i18n/locales/pl/app/course-slugs.json" with { type: "json" };
import elCourseSlugs from "../i18n/locales/el/app/course-slugs.json" with { type: "json" };
import frCourseSlugs from "../i18n/locales/fr/app/course-slugs.json" with { type: "json" };
import slCourseSlugs from "../i18n/locales/sl/app/course-slugs.json" with { type: "json" };
import { DEFAULT_FALLBACK_LANG, TARGET_LANGS as REGISTRY_TARGET_LANGS, UI_LANGS as REGISTRY_UI_LANGS } from "./locale-registry.js";
import { COURSE_TARGET_LANDING, COURSE_TARGET_LANGS, COURSE_TARGET_SLUGS, courseInterfaceLanguage, courseInterfaceLanguages, scriptInterfaceLanguage, scriptInterfaceLanguages } from "./course-targets.js";
import { SCRIPT_COURSE_SLUGS } from "../features/course/script/slug-map.js";
import { COURSE_ROUTE_LOCALES, COURSE_ROUTE_SEGMENTS, LEGACY_COURSE_SECTIONS } from "./course-route-slugs.js";
export { COURSE_ROUTE_SEGMENTS } from "./course-route-slugs.js";

/** Interface (URL prefix) languages — UI copy and chrome. */
export const UI_LANGS = REGISTRY_UI_LANGS;

/** UI langs that use a `/{lang}/…` URL prefix (all except default `de`). */
export const UI_PATH_PREFIX_LANGS = UI_LANGS.filter((l) => l !== "de");

/** Learning target language codes supported in URLs and content. */
export const TARGET_LANGS = REGISTRY_TARGET_LANGS;

/**
 * Target langs included in the static site build (prerender, sitemap, scene API).
 * English (`en`) remains in {@link TARGET_LANGS} for content/registry, but its
 * learner pages are not shipped until that track is ready.
 */
export const TARGET_LANGS_BUILD = Object.freeze(["de", "ch"]);

/** Subset offered in the nav target-language menu and home-page CTAs. */
export const TARGET_LANGS_UI = TARGET_LANGS_BUILD;

/** Target language slug (what you learn) per UI language — ASCII only. */
const TARGET_SEGMENTS_BY_UI = {
  en: { de: "german", en: "english", ch: "german-switzerland" },
  de: { de: "deutsch", en: "englisch", ch: "deutsch-schweiz" },
  ar: { de: "almaniyya", en: "ingliziyya", ch: "almaniyya-swisriyya" },
  tr: { de: "almanca", en: "ingilizce", ch: "almanca-isvicre" },
  uk: { de: "nimetska", en: "anhliiska", ch: "nimetska-shveitsarska" },
  ru: { de: "nemetskiy", en: "angliyskiy", ch: "nemetskiy-shveytsariya" },
  es: { de: "aleman", en: "ingles", ch: "aleman-suizo" },
  vi: { de: "tieng-duc", en: "tieng-anh", ch: "tieng-duc-thuy-si" },
  pl: { de: "niemiecki", en: "angielski", ch: "niemiecki-szwajcarski" },
  el: { de: "germanika", en: "anglika", ch: "germanika-elvetias" },
  fr: { de: "allemand", en: "anglais", ch: "allemand-suisse" },
  sl: { de: "nemscina", en: "anglescina", ch: "nemscina-svica" },
};

/** “Learn …” landing slug per UI language — ASCII only. */
const TARGET_LANDING_BY_UI = {
  en: { de: "learn-german", en: "learn-english", ch: "learn-german-switzerland" },
  de: { de: "deutsch-lernen", en: "englisch-lernen", ch: "deutsch-schweiz-lernen" },
  ar: { de: "taallam-almaniyya", en: "taallam-ingliziyya", ch: "taallam-almaniyya-swisriyya" },
  tr: { de: "almanca-ogren", en: "ingilizce-ogren", ch: "almanca-isvicre-ogren" },
  uk: { de: "vyuchay-nimetsku", en: "vyuchay-angliisku", ch: "vyuchay-nimetsku-shveitsarsku" },
  ru: { de: "uchit-nemetskiy", en: "uchit-angliyskiy", ch: "uchit-nemetskiy-shveytsariya" },
  es: { de: "aprender-aleman", en: "aprender-ingles", ch: "aprender-aleman-suizo" },
  vi: { de: "hoc-tieng-duc", en: "hoc-tieng-anh", ch: "hoc-tieng-duc-thuy-si" },
  pl: { de: "uczyc-sie-niemieckiego", en: "uczyc-sie-angielskiego", ch: "uczyc-sie-niemieckiego-szwajcarskiego" },
  el: { de: "mathaino-germanika", en: "mathaino-anglika", ch: "mathaino-germanika-elvetias" },
  fr: { de: "apprendre-allemand", en: "apprendre-anglais", ch: "apprendre-allemand-suisse" },
  sl: { de: "uciti-nemscino", en: "uciti-anglescino", ch: "uciti-nemscino-svica" },
};

/** Content section slug per UI language — ASCII only. */
const SECTION_SEGMENTS_BY_UI = {
  en: {
    adventures: "adventures",
    stories: "stories",
    dialogs: "dialogs",
    scenes: "scenes",
    vocabulary: "vocabulary",
    grammar: "grammar",
  },
  de: {
    adventures: "abenteuer",
    stories: "geschichten",
    dialogs: "dialoge",
    scenes: "szenen",
    vocabulary: "wortschatz",
    grammar: "grammatik",
  },
  ar: {
    adventures: "mughamarat",
    stories: "qusus",
    dialogs: "hiwarat",
    scenes: "mashahid",
    vocabulary: "mufradat",
    grammar: "qawaed",
  },
  tr: {
    adventures: "maceralar",
    stories: "hikayeler",
    dialogs: "diyaloglar",
    scenes: "sahneler",
    vocabulary: "kelime-hazinesi",
    grammar: "gramer",
  },
  uk: {
    adventures: "pryhody",
    stories: "istorii",
    dialogs: "dialohy",
    scenes: "stseny",
    vocabulary: "leksyka",
    grammar: "hramatyka",
  },
  ru: {
    adventures: "priklyucheniya",
    stories: "istorii",
    dialogs: "dialogi",
    scenes: "stseny",
    vocabulary: "leksika",
    grammar: "grammatika",
  },
  es: {
    adventures: "aventuras",
    stories: "historias",
    dialogs: "dialogos",
    scenes: "escenas",
    vocabulary: "vocabulario",
    grammar: "gramatica",
  },
  vi: {
    adventures: "phieu-luu",
    stories: "truyen",
    dialogs: "hoi-thoai",
    scenes: "canh",
    vocabulary: "tu-vung",
    grammar: "ngu-phap",
  },
  pl: {
    adventures: "przygody",
    stories: "historie",
    dialogs: "dialogi",
    scenes: "sceny",
    vocabulary: "slownictwo",
    grammar: "gramatyka",
  },
  el: {
    adventures: "peripeteies",
    stories: "istories",
    dialogs: "dialogoi",
    scenes: "skines",
    vocabulary: "lexilogio",
    grammar: "grammatiki",
  },
  fr: {
    adventures: "aventures",
    stories: "histoires",
    dialogs: "dialogues",
    scenes: "scenes",
    vocabulary: "vocabulaire",
    grammar: "grammaire",
  },
  sl: {
    adventures: "pustolovscine",
    stories: "zgodbe",
    dialogs: "dialogi",
    scenes: "prizori",
    vocabulary: "besedisce",
    grammar: "slovnica",
  },
};

export const TARGET_SEGMENTS = TARGET_SEGMENTS_BY_UI;
export const TARGET_LANDING_SEGMENTS = TARGET_LANDING_BY_UI;
export const SECTION_SEGMENTS = SECTION_SEGMENTS_BY_UI;

/** Course language segment per interface, from locales/{ui}/app/course-slugs.json. */
const COURSE_SLUGS_BY_UI = {
  de: deCourseSlugs,
  en: enCourseSlugs,
  ar: arCourseSlugs,
  tr: trCourseSlugs,
  uk: ukCourseSlugs,
  ru: ruCourseSlugs,
  es: esCourseSlugs,
  vi: viCourseSlugs,
  pl: plCourseSlugs,
  el: elCourseSlugs,
  fr: frCourseSlugs,
  sl: slCourseSlugs,
};

export const TARGET_SECTION_AVAILABILITY = {
  /** Sections offered on this target. The nav template orders them: Kurs, Schrift, then Geschichten through Wortschatz. */
  de: ["stories", "dialogs", "grammar", "adventures", "vocabulary", "scenes", "course"],
  en: ["course"],
  ch: ["stories"],
};

for (const ui of Object.keys(TARGET_SEGMENTS_BY_UI)) {
  const targets = TARGET_SEGMENTS_BY_UI[ui];
  const landings = TARGET_LANDING_BY_UI[ui];
  for (const [code, slug] of Object.entries(COURSE_TARGET_SLUGS)) {
    if (!targets[code]) targets[code] = COURSE_SLUGS_BY_UI[ui]?.[code] || slug;
  }
  for (const [code, slug] of Object.entries(COURSE_TARGET_LANDING)) {
    if (!landings[code]) landings[code] = slug;
  }
  SECTION_SEGMENTS_BY_UI[ui].course = COURSE_ROUTE_LOCALES[ui].course;
}

export const courseRouteSegment = (ui, key) => COURSE_ROUTE_SEGMENTS[ui]?.[key] || key;

const SCRIPT_SECTION_WORDS = ["script", ...Object.values(COURSE_ROUTE_SEGMENTS).map((map) => map.script).filter(Boolean)];

/** `script` is the hub. `script-devanagari` is one reading course, in any interface's word for script. */
export function matchScriptSection(segment) {
  const token = String(segment || "").trim().toLowerCase();
  for (const word of SCRIPT_SECTION_WORDS) {
    if (token === word) return { key: "script", name: "" };
    if (token.startsWith(`${word}-`)) return { key: "script", name: token.slice(word.length + 1) };
  }
  return null;
}

function isScriptRouteSlug(slug) {
  return slug === "script" || String(slug || "").startsWith("script-");
}

/** Writing systems that are not simply the language's own name. */
const DISTINCT_SCRIPT_SLUGS = new Set(["devanagari", "hiragana", "katakana", "hangul"]);

function namedScriptSlug(name) {
  return DISTINCT_SCRIPT_SLUGS.has(name) ? `script-${name}` : "script";
}

/** One distinct script is named in the URL. Several keep the plain script hub. A script that is just the language stays on that hub. */
export function canonicalScriptSlug(targetCode) {
  const courses = SCRIPT_COURSE_SLUGS[targetCode] || [];
  const named = [...new Set(courses.map(namedScriptSlug).filter((slug) => slug !== "script"))];
  return named.length === 1 && courses.length === 1 ? named[0] : "script";
}

function publicScriptKey(targetCode, slug) {
  if (!slug || slug === "script") return canonicalScriptSlug(targetCode);
  const name = String(slug).startsWith("script-") ? slug.slice("script-".length) : "";
  return namedScriptSlug(name);
}

export function scriptSectionSegment(ui, slug) {
  const name = String(slug || "").startsWith("script-") ? slug.slice("script-".length) : "";
  return `${courseRouteSegment(ui, "script")}${name ? `-${name}` : ""}`;
}
export function courseRouteKey(ui, segment) {
  const token = String(segment || "").trim();
  if (!token) return token;
  const alias = COURSE_ROUTE_LOCALES[ui]?.aliases?.[token];
  if (alias) return alias;
  const maps = [COURSE_ROUTE_SEGMENTS[ui], ...Object.values(COURSE_ROUTE_SEGMENTS)].filter(Boolean);
  for (const map of maps) {
    const found = Object.entries(map).find(([, value]) => value === token)?.[0];
    if (found) return found;
  }
  return token;
}

// Preserve old landing URLs as aliases while every interface gets its own canonical slug.
const LEGACY_TARGET_LANDING_BY_UI = Object.fromEntries(Object.entries(TARGET_LANDING_BY_UI).map(([ui, slugs]) => [ui, {...slugs}]));
for (const [ui, slugs] of Object.entries(localizedLandingSlugs)) Object.assign(TARGET_LANDING_BY_UI[ui], slugs);

for (const code of COURSE_TARGET_LANGS) {
  if (!TARGET_SECTION_AVAILABILITY[code]) TARGET_SECTION_AVAILABILITY[code] = ["course"];
}

export function isUiLang(code) {
  return UI_LANGS.includes(code);
}

function normalizeToken(value) {
  return String(value || "").trim().toLowerCase().normalize("NFC");
}

function stripDiacritics(value) {
  return normalizeToken(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/** URL segment equality: case/diacritic-insensitive. */
export function segmentTokensMatch(candidate, input) {
  const a = normalizeToken(candidate);
  const b = normalizeToken(input);
  if (!a || !b) return false;
  if (a === b) return true;
  return stripDiacritics(a) === stripDiacritics(b);
}

function uniqueMapsForUi(uiLang) {
  const safe = UI_LANGS.includes(uiLang) ? uiLang : DEFAULT_FALLBACK_LANG;
  const out = [];
  const push = (m) => {
    if (m && !out.includes(m)) out.push(m);
  };
  push(TARGET_SEGMENTS_BY_UI[safe]);
  push(TARGET_SEGMENTS_BY_UI.de);
  push(TARGET_SEGMENTS_BY_UI.en);
  push(TARGET_LANDING_BY_UI[safe]);
  push(TARGET_LANDING_BY_UI.de);
  push(TARGET_LANDING_BY_UI.en);
  push(LEGACY_TARGET_LANDING_BY_UI[safe]);
  push(LEGACY_TARGET_LANDING_BY_UI.de);
  push(LEGACY_TARGET_LANDING_BY_UI.en);
  return out;
}

function sectionMapsForUi(uiLang) {
  const safe = UI_LANGS.includes(uiLang) ? uiLang : DEFAULT_FALLBACK_LANG;
  const out = [];
  const push = (m) => {
    if (m && !out.includes(m)) out.push(m);
  };
  push(SECTION_SEGMENTS_BY_UI[safe]);
  push(SECTION_SEGMENTS_BY_UI.de);
  push(SECTION_SEGMENTS_BY_UI.en);
  push(legacyUrlSlugs.sections[safe]);
  return out;
}

export function getTargetCodeFromSegment(uiLang, segment) {
  for (const map of uniqueMapsForUi(uiLang)) {
    for (const [code, seg] of Object.entries(map)) {
      if (segmentTokensMatch(seg, segment)) return code;
    }
  }
  const safe = UI_LANGS.includes(uiLang) ? uiLang : DEFAULT_FALLBACK_LANG;
  return legacyUrlSlugs.targets[safe]?.[normalizeToken(segment)] || null;
}

export function getSectionKeyFromSegment(uiLang, segment) {
  return parseSectionSegment(uiLang, segment).sectionKey;
}

export function parseSectionSegment(uiLang, rawSegment) {
  const segment = normalizeToken(rawSegment || "");
  if (["course", "course-a1", "kurs", ...LEGACY_COURSE_SECTIONS, ...Object.values(COURSE_ROUTE_LOCALES).map(locale => locale.course)].includes(segment)) return { sectionKey: "course", level: null };
  const segmentFolded = stripDiacritics(segment);
  for (const map of sectionMapsForUi(uiLang)) {
    for (const [key, baseSeg] of Object.entries(map)) {
      const base = normalizeToken(baseSeg);
      const baseFolded = stripDiacritics(baseSeg);
      if (segment === base || segmentFolded === baseFolded) return { sectionKey: key, level: null };
      if (segment.startsWith(`${base}-`) || segmentFolded.startsWith(`${baseFolded}-`)) {
        const useAscii = segment.startsWith(`${base}-`);
        const cutLen = useAscii ? base.length + 1 : baseFolded.length + 1;
        const source = useAscii ? segment : segmentFolded;
        const suffix = source.slice(cutLen).trim();
        if (!suffix) return { sectionKey: key, level: null };
        return { sectionKey: key, level: suffix.toUpperCase() };
      }
    }
  }
  return { sectionKey: null, level: null };
}

export function getTargetSegment(uiLang, targetCode) {
  const safe = UI_LANGS.includes(uiLang) ? uiLang : DEFAULT_FALLBACK_LANG;
  return TARGET_SEGMENTS_BY_UI[safe]?.[targetCode] || null;
}

export function getTargetLandingSegment(uiLang, targetCode) {
  const safe = UI_LANGS.includes(uiLang) ? uiLang : DEFAULT_FALLBACK_LANG;
  return (
    TARGET_LANDING_BY_UI[safe]?.[targetCode] ||
    TARGET_SEGMENTS_BY_UI[safe]?.[targetCode] ||
    null
  );
}

export function getSectionSegment(uiLang, sectionKey) {
  const safe = UI_LANGS.includes(uiLang) ? uiLang : DEFAULT_FALLBACK_LANG;
  return SECTION_SEGMENTS_BY_UI[safe]?.[sectionKey] || null;
}

export function getAvailableSections(targetCode) {
  return TARGET_SECTION_AVAILABILITY[targetCode] || [];
}

/** Space-separated target codes that offer this nav section. */
export function navLangList(sectionKey) {
  return Object.entries(TARGET_SECTION_AVAILABILITY)
    .filter(([, sections]) => sections.includes(sectionKey))
    .map(([code]) => code)
    .join(" ");
}

export function isSectionAvailable(targetCode, sectionKey) {
  return getAvailableSections(targetCode).includes(sectionKey);
}

/**
 * From URL: first path segment is `en`/`ar`/…, or a German-default segment (no `de` prefix) → "de".
 */
export function getRouteUiLangFromPath(ctx) {
  const p0 = (ctx?.url?.pathname || "").split("/").filter(Boolean)[0] || "";
  /** Prefixed routes (`/vi/…`, `/en/…`) must follow the URL, not stale handler params. */
  if (isUiLang(p0)) return p0;
  const fromParam = String(ctx?.params?.uiLang || "").trim().toLowerCase();
  if (isUiLang(fromParam)) return fromParam;
  return "de";
}

/** Path prefix for non-default (German) interface languages only. */
export function pathPrefixForUiLang(uiLang) {
  const safe = UI_LANGS.includes(uiLang) ? uiLang : "de";
  return safe === "de" ? "" : `/${safe}`;
}

/**
 * Login page URL that opens the free registration panel (`view=register`).
 * @param {string} uiLang
 * @param {{ next?: string, signupOrigin?: string, signupPage?: string }} [opts]
 */
export function buildLoginRegisterHref(uiLang, opts = {}) {
  const safeUi = UI_LANGS.includes(uiLang) ? uiLang : "de";
  const q = new URLSearchParams();
  q.set("uiLang", safeUi);
  q.set("view", "register");
  const next = String(opts.next || "").trim();
  if (next) q.set("next", next);
  const signupOrigin = String(opts.signupOrigin || "").trim();
  const signupPage = String(opts.signupPage || "").trim();
  if (signupOrigin) q.set("signupOrigin", signupOrigin);
  if (signupPage) q.set("signupPage", signupPage);
  const qs = q.toString();
  // Login is an unprefixed route that reads `?uiLang`; `/<lang>/login` would 404 → home.
  return `/login${qs ? `?${qs}` : ""}`;
}

export function buildLocalizedPath({
  uiLang,
  targetCode,
  sectionKey,
  level = null,
  slug = null,
  sceneSlug = null,
}) {
  const scriptRoute = sectionKey === "course" && isScriptRouteSlug(slug);
  const safeUi = sectionKey === "course" ? (scriptRoute ? scriptInterfaceLanguage : courseInterfaceLanguage)(uiLang, targetCode) : (UI_LANGS.includes(uiLang) ? uiLang : "de");
  const targetSeg = getTargetSegment(safeUi, targetCode);
  const sectionSeg = getSectionSegment(safeUi, sectionKey);
  if (!targetSeg || !sectionSeg) return "/";
  const levelSuffix = sectionKey !== "course" && level ? `-${String(level).toLowerCase()}` : "";
  const pfx = pathPrefixForUiLang(safeUi);
  if (scriptRoute) {
    const key = publicScriptKey(targetCode, slug);
    return `${pfx}/${targetSeg}/${scriptSectionSegment(safeUi, key)}${sceneSlug ? `/${courseRouteSegment(safeUi, sceneSlug)}` : ""}`.replace(/\/+/g, "/");
  }
  let path = `${pfx}/${targetSeg}/${sectionSeg}${levelSuffix}`.replace(/\/+/g, "/");
  if (slug) path += `/${sectionKey === "course" ? courseRouteSegment(safeUi, slug) : slug}`;
  if (sceneSlug) path += `/${sectionKey === "course" ? courseRouteSegment(safeUi, sceneSlug) : sceneSlug}`;
  return path;
}

/** URL path to the German grammar hub (Grammatik) for the given UI language. */
export function getGermanGrammatikPath(uiLang) {
  return buildLocalizedPath({
    uiLang: UI_LANGS.includes(uiLang) ? uiLang : "de",
    targetCode: "de",
    sectionKey: "grammar",
  });
}

/** URL path to the German lexicon (Wortschatz) for the given UI language. */
export function getGermanVocabularyPath(uiLang) {
  return buildLocalizedPath({
    uiLang,
    targetCode: "de",
    sectionKey: "vocabulary",
  });
}

function toAbsoluteHref(origin, path) {
  if (!origin) return path;
  try {
    return new URL(path, origin).toString();
  } catch {
    return path;
  }
}

export function buildAlternateHreflangs({
  targetCode,
  sectionKey,
  slug = null,
  sceneSlug = null,
  level = null,
  slugsByUiLang = null,
  origin = null,
}) {
  if (!isSectionAvailable(targetCode, sectionKey)) return [];
  const normalizedLevel = level ? String(level).trim().toLowerCase() : null;
  /** @type {Array<{ hreflang: string, href: string }>} */
  const alternates = [];
  for (const uiLang of sectionKey === "course" ? (isScriptRouteSlug(slug) ? scriptInterfaceLanguages : courseInterfaceLanguages)(targetCode) : UI_LANGS) {
    const effectiveSlug = slugsByUiLang?.[uiLang] ?? slug;
    if (slugsByUiLang && !slugsByUiLang[uiLang]) continue;
    const path = buildLocalizedPath({
      uiLang,
      targetCode,
      sectionKey,
      level: normalizedLevel,
      slug: effectiveSlug,
      sceneSlug,
    });
    alternates.push({
      hreflang: uiLang,
      href: toAbsoluteHref(origin, path),
    });
  }
  return alternates;
}

/** `hreflang` alternates for UI-language home pages (`/`, `/en/`, `/uk/`, …). */
export function buildUiLangHomeAlternates({ origin = null } = {}) {
  return UI_LANGS.map((uiLang) => {
    const path = pathPrefixForUiLang(uiLang) || "/";
    return { hreflang: uiLang, href: toAbsoluteHref(origin, path) };
  });
}

/** `hreflang` alternates for “learn German/English/…” target landing pages. */
export function buildTargetLandingAlternates({ targetCode, origin = null }) {
  /** @type {Array<{ hreflang: string, href: string }>} */
  const alternates = [];
  for (const uiLang of UI_LANGS) {
    const seg = getTargetLandingSegment(uiLang, targetCode);
    if (!seg) continue;
    const pfx = pathPrefixForUiLang(uiLang);
    const path = `${pfx}/${seg}`.replace(/\/+/g, "/");
    alternates.push({ hreflang: uiLang, href: toAbsoluteHref(origin, path) });
  }
  return alternates;
}

/**
 * Ensure the active UI language has an `hreflang` entry pointing at the current URL
 * (pathname + search), as required for valid alternate sets.
 */
export function ensureAlternateSelfReference(alternates, uiLang, pathname, search = "", origin = null) {
  const safeUi = UI_LANGS.includes(uiLang) ? uiLang : "de";
  const path = (String(pathname || "").trim() || "/") + String(search || "");
  const selfHref = toAbsoluteHref(origin, path);
  const out = Array.isArray(alternates) ? alternates.map((alt) => ({ ...alt })) : [];
  const idx = out.findIndex((alt) => alt.hreflang === safeUi);
  if (idx >= 0) {
    out[idx] = { hreflang: safeUi, href: selfHref };
  } else {
    out.push({ hreflang: safeUi, href: selfHref });
  }
  return out;
}

/**
 * Precomputed canonical paths for the nav language switcher (no `?cid=` bootstrap).
 * @param {object} opts
 * @param {string} opts.targetCode
 * @param {string} opts.uiLang Current interface language
 * @param {string} opts.sectionKey
 * @param {string | null} [opts.slug]
 * @param {string | null} [opts.sceneSlug]
 * @param {string | null} [opts.level]
 * @param {Record<string, string> | null} [opts.slugsByUiLang] Grammar unit slugs per UI lang
 * @param {string | null} [opts.contentId]
 * @param {string | null} [opts.sceneContentId]
 */
export function buildRouteContentHint({
  targetCode,
  uiLang,
  sectionKey,
  slug = null,
  sceneSlug = null,
  level = null,
  slugsByUiLang = null,
  contentId = null,
  sceneContentId = null,
}) {
  const sec = String(sectionKey || "").trim();
  const safeUi = UI_LANGS.includes(uiLang) ? uiLang : "de";
  const normalizedLevel = level ? String(level).trim().toLowerCase() : null;

  /** @type {Record<string, string>} */
  const pathsByUiLang = {};
  for (const ui of UI_LANGS) {
    const effectiveSlug = slugsByUiLang?.[ui] ?? slug;
    if (!effectiveSlug) continue;
    if (slugsByUiLang && !slugsByUiLang[ui]) continue;
    if (!isSectionAvailable(targetCode, sec)) continue;
    pathsByUiLang[ui] = buildLocalizedPath({
      uiLang: ui,
      targetCode,
      sectionKey: sec,
      level: normalizedLevel,
      slug: effectiveSlug,
      sceneSlug,
    });
  }

  /** @type {Record<string, string>} */
  const pathsByTargetCode = {};
  const effectiveSlugForTarget = slugsByUiLang?.[safeUi] ?? slug;
  if (effectiveSlugForTarget) {
    for (const tCode of TARGET_LANGS_BUILD) {
      if (!isSectionAvailable(tCode, sec)) continue;
      pathsByTargetCode[tCode] = buildLocalizedPath({
        uiLang: safeUi,
        targetCode: tCode,
        sectionKey: sec,
        level: normalizedLevel,
        slug: effectiveSlugForTarget,
        sceneSlug,
      });
    }
  }

  return {
    contentId: contentId || null,
    sceneContentId: sceneContentId || null,
    sectionKey: sec,
    pathsByUiLang,
    pathsByTargetCode,
  };
}

export function getXDefaultHref(alternates) {
  if (!Array.isArray(alternates) || alternates.length === 0) return null;
  const deAlt = alternates.find((alt) => alt.hreflang === "de");
  const enAlt = alternates.find((alt) => alt.hreflang === "en");
  return deAlt?.href || enAlt?.href || alternates[0].href || null;
}

/** `/schulen` for German UI; `?uiLang=` for all other interface languages. */
export function buildSchoolsHref(uiLang) {
  const lang = String(uiLang || "de").trim().toLowerCase() || "de";
  if (lang === "de") return "/schulen";
  return `/schulen?uiLang=${encodeURIComponent(lang)}`;
}
